import {
	EventV2Response,
	EventSession,
	restRepository,
	EventState,
	EventStatusV2,
	EventTypeV2,
	EventDuration,
	OrderByEnum,
	CheckinQrTicketDto,
	LocationType,
} from "@onair/repositories";

export interface IGetMineEventsV2Params {
	offset?: number;
	limit?: number;
	statuses?: EventStatusV2[];
	search?: string;
	startDate?: string;
	endDate?: string;
	minPrice?: string;
	maxPrice?: string;
	state?: EventState;
	location?: string;
	eventType?: EventTypeV2;
}

export interface IGetPublicEventsV2Params {
	offset?: number;
	limit?: number;
	categories?: string;
	business?: string;
	search?: string;
	startDate?: string;
	endDate?: string;
	state?: EventState;
	type?: EventTypeV2;
	duration?: EventDuration;
	order?: OrderByEnum;
	orderField?: string;
}

export interface IGetMyCoHostEventsCountingParams {
	type?: EventTypeV2;
	searchValue?: string;
}

const getEventSessionById = async (id: string): Promise<EventSession> => {
	const res = await restRepository.api.getEventSessionById(id);
	return res.data;
};

const getEventV2ById = async (id: string): Promise<EventV2Response> => {
	const res = await restRepository.api.getEventV2ById(id);
	return res.data;
};

const getEventV2BySlug = async (slug: string): Promise<EventV2Response> => {
	const res = await restRepository.api.getEventBySlugV2(slug);
	return res.data;
};

const getEventByTicketTierId = async (
	ticketTierId: string,
): Promise<EventV2Response> => {
	const res = await restRepository.api.getEventByTicketTierId(ticketTierId);
	return res.data;
}

const getMyCoHostEvents = async (params: IGetMineEventsV2Params) => {
	const res = await restRepository.api.getMyCoHostEvents({
		offset: params.offset || 1,
		limit: params.limit || 10,
		statuses: params.statuses ? params.statuses.join(",") : "",
		search: params.search || "",
		startDate: params.startDate || "",
		endDate: params.endDate || "",
		state: params.state || EventState.All,
		minPrice: params.minPrice || "",
		maxPrice: params.maxPrice || "",
	});

	return res.data;
};

const getMyCoHostEventsCounting = async (
	params: IGetMyCoHostEventsCountingParams,
) => {
	const res = await restRepository.api.getMyCoHostEventsCounting({
		type: params.type || undefined,
		searchValue: params.searchValue || "",
	});

	return res.data;
};

const getPublicEvents = async (params: IGetPublicEventsV2Params) => {
	const { data } = await restRepository.api.getPublicEvents({
		offset: params.offset || 1,
		limit: params.limit || 10,
		order: params.order || undefined,
		orderField: params.orderField || undefined,
		search: params.search || "",
		startDate: params.startDate || "",
		endDate: params.endDate || "",
		state: params.state || EventState.All,
		type: params.type || undefined,
		duration: params.duration || undefined,
		business: params.business || undefined,
		categories: params.categories || undefined,
	});

	return data;
};

const getPublicEventsAndCombosCache = async (
	params: IGetPublicEventsV2Params,
) => {
	const { data } = await restRepository.api.getEventsAndCombosCached({
		offset: params.offset || 1,
		limit: params.limit || 10,
		order: params.order || undefined,
		orderField: params.orderField || undefined,
		search: params.search || "",
		startDate: params.startDate || "",
		endDate: params.endDate || "",
		state: params.state || EventState.All,
		contentType: params.type || undefined,
		duration: params.duration || undefined,
		business: params.business || undefined,
		categories: params.categories || undefined,
	});

	return data;
};

export interface PublishEventAndComboQueryFilter {
	filter?: string;
	cursor?: string;
	order?: OrderByEnum;
	orderField?: string;
	offset?: number;
	limit?: number;
	state?: EventState;
	search?: string;
	duration?: EventDuration;
	type?: EventTypeV2;
	business?: string;
	categories?: string;
	startDate?: string;
	endDate?: string;
	contentType?: EventTypeV2;
	price?: string;
	locationType?: LocationType,
}

const getPublishEventsAndCombos = async (
	query?: PublishEventAndComboQueryFilter,
) => {
	const { data } = await restRepository.api.getPublicEventsAndCombos(query);
	return data;
};

const getEventsAndCombosCached = async (
	query: PublishEventAndComboQueryFilter,
) => {
	const { data } = await restRepository.api.getEventsAndCombosCached(query);
	return data;
};

export interface EncryptEventDataToCheckinPayload {
	userId: string;
	eventId: string;
}
// const encryptEventDataToCheckin = async (
// 	payload: EncryptEventDataToCheckinPayload,
// ) => {
// 	const { data } = await restRepository.api.encryptQrTicket(payload);
// 	return data;
// };
const doCheckinEvent = async (payload: CheckinQrTicketDto) => {
	const { data } = await restRepository.api.checkinQrTicket(payload);
	return data;
};

export interface EventOfflineSuccessFilterPayload {
	itemId: string;
	query?: {
		filter?: string;
		cursor?: string;
		order?: OrderByEnum;
		orderField?: string;
		offset?: number;
		limit?: number;
	};
}
const getUsersBuyedTicketsEventOffline = async (
	payload: EventOfflineSuccessFilterPayload,
) => {
	const { data } = await restRepository.api.getSuccessOrdersEventOffline(
		payload.itemId,
		{ ...payload?.query },
	);
	return data;
};

const getEventTrendOnEventDetail = async (
	eventId: string,
) => {
	const { data } = await restRepository.api.queryEventTrendOnEventDetail(
		eventId
	);
	return data;
};

const getEventTrendOnCart = async () => {
	const { data } = await restRepository.api.queryEventTrendOnCart();
	return data;
};

export {
	getPublicEvents,
	getEventV2BySlug,
	getEventV2ById,
	getEventSessionById,
	getEventByTicketTierId,
	getMyCoHostEvents,
	getMyCoHostEventsCounting,
	getPublicEventsAndCombosCache,
	getPublishEventsAndCombos,
	getEventsAndCombosCached,
	doCheckinEvent,
	getUsersBuyedTicketsEventOffline,
	getEventTrendOnEventDetail,
	getEventTrendOnCart
};
